import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig } from 'axios';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string | undefined,
});

let msalInstance: PublicClientApplication | undefined;

export function setMsalForRequests(instance: PublicClientApplication) {
  msalInstance = instance;
}

export const request = async <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const response = await axiosInstance.request<T>({ ...config, ...options });
  return response.data;
};

// Like request, but with the whole response, for example to read the headers of a download.
export const requestWithResponse = <T>(config: AxiosRequestConfig) => axiosInstance.request<T>(config);

axiosInstance.interceptors.request.use(
  async (config) => {
    const headers = AxiosHeaders.from(config.headers);

    if (!msalInstance) {
      headers.set('LZTB-CSRF-PROTECTION', '1');
      config.headers = headers;
      return config;
    }

    const account = msalInstance.getActiveAccount();

    if (account) {
      const tokenRequest = {
        account,
        scopes: [`api://${msalInstance.getConfiguration().auth.clientId}/TB.Write`],
      };

      try {
        const response = await msalInstance.acquireTokenSilent(tokenRequest);
        headers.set('Authorization', `Bearer ${response.accessToken}`);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          const response = await msalInstance.acquireTokenPopup(tokenRequest);
          headers.set('Authorization', `Bearer ${response.accessToken}`);
        } else {
          throw error;
        }
      }
    }

    headers.set('LZTB-CSRF-PROTECTION', '1');
    config.headers = headers;

    return config;
  },
  (error) => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;